import { Component, Input, OnInit } from '@angular/core';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import { Signal } from '@models/dashboard/signal';
import { Machine } from '@models/machine/machine';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ChartsConfigurationHelperService } from '@services/charts/helper/charts-configuration-helper.service';
import { ModalDialogDismissOption } from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

@Component({
    selector: 'scitis-widget-configuration-modal',
    templateUrl: './widget-configuration-modal.component.html',
    styleUrls: ['./widget-configuration-modal.component.scss'],
    standalone: false
})
export class WidgetConfigurationModalComponent implements OnInit {

  @Input()
  isAddOperation: boolean = false;

  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();

  @Input()
  machine: Machine = new Machine();

  @Input()
  existingWidgetNames: string[] = [];

  @Input()
  existingWidgetTypes: UserDefinedWidgetType[] = [];

  @Input()
  isTemplateWidget: boolean = false;

  machineSignalsArray: Signal[] = [];

  isChartSetting: boolean = false;
  widgetTypes = UserDefinedWidgetType;
  usableUserDefinedPageChartTypes: UserDefinedWidgetType[] = [
    UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS, UserDefinedWidgetType.GAUGE, UserDefinedWidgetType.TEXT, UserDefinedWidgetType.BAR,
    UserDefinedWidgetType.STATUS, UserDefinedWidgetType.IMAGE, UserDefinedWidgetType.MAP, UserDefinedWidgetType.COMMAND,
    UserDefinedWidgetType.PIE

    //TODO Enable when new widget types are added

    //
    // , ChartType.ANALYSIS_LINE
  ];
  public widgetTypeCssClass: string = '';
  public widgetTypeTitle: string = '';
  readonly UserDefinedWidgetType = UserDefinedWidgetType;

  private validLineWidgetConfiguration: boolean = true;
  private validGaugeWidgetConfiguration: boolean = true;
  private validTextWidgetConfiguration: boolean = true;
  private validBarWidgetConfiguration: boolean = true;
  private validPieWidgetConfiguration: boolean = true;
  private validStatusWidgetConfiguration: boolean = true;
  private validImageWidgetConfiguration: boolean = true;
  private validMapWidgetConfiguration: boolean = false;
  private validCommandWidgetConfiguration: boolean = false;
  private hasGenericGatewaySelected: boolean = false;
  private hasDeletedSignalsMap: Map<string, boolean> = new Map<string, boolean>();


  constructor(
      private activeModal: NgbActiveModal,
      private chartsConfigurationHelper: ChartsConfigurationHelperService,
      private translate: TranslateService,
  ) {
    this.chartsConfigurationHelper.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignalsMap = hasDeletedSignals;
    });
  }

  public get isWidgetNameNotSet(): boolean {
    return !this.userDefinedWidget.widgetDetails.widgetName || this.userDefinedWidget.widgetDetails.widgetName.isNullOrWhitespace();
  }

  public get widgetNameIsUsed(): boolean {
    return this.existingWidgetNames.filter(m =>
        this.trimLowerSpace(m) === this.trimLowerSpace(this.userDefinedWidget.widgetDetails.widgetName)).length > 0;
  }

  public get widgetNameTooLong(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetName.length > 50;
  }


  ngOnInit(): void {
    this.widgetTypeCssClass = this.chartsConfigurationHelper.determineWidgetTypeIconCssClass(this.userDefinedWidget.widgetDetails.widgetType);
    this.widgetTypeTitle = this.chartsConfigurationHelper.determineTitle(this.userDefinedWidget.widgetDetails.widgetType);
    this.machineSignalsArray = this.convertSignalsObjectToArray(this.machine.signals);
    if (!this.isAddOperation) {
      this.isChartSetting = true;
    }
  }

  onClose(): void {
    this.chartsConfigurationHelper.hasDeletedSignals$.next(new Map<string, boolean>());
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(_$event): void {
    this.chartsConfigurationHelper.hasDeletedSignals$.next(new Map<string, boolean>());
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave(_$event: boolean): void {
    this.activeModal.close(this.userDefinedWidget);
  }

  toggleChartSettings() {
    this.isChartSetting = !this.isChartSetting;
  }

  onSelectWidgetContentType($event: string) {
    this.userDefinedWidget.widgetDetails.widgetType = this.widgetTypes[$event];
    this.widgetTypeCssClass = this.chartsConfigurationHelper.determineWidgetTypeIconCssClass(this.userDefinedWidget.widgetDetails.widgetType);
    this.widgetTypeTitle = this.chartsConfigurationHelper.determineTitle(this.userDefinedWidget.widgetDetails.widgetType);
    this.toggleChartSettings();
  }

  isLineWidgetConfigurationValid($event: boolean) {
    this.validLineWidgetConfiguration = $event;
  }

  isMapWidgetConfigurationValid($event: boolean) {
    this.validMapWidgetConfiguration = $event;
  }

  isGaugeConfigurationValid($event: boolean) {
    this.validGaugeWidgetConfiguration = $event;
  }

  isTextWidgetConfigurationValid($event: boolean) {
    this.validTextWidgetConfiguration = $event;
  }

  isBarWidgetConfigurationValid($event: boolean) {
    this.validBarWidgetConfiguration = $event;
  }

  isPieWidgetConfigurationValid($event: boolean) {
    this.validPieWidgetConfiguration = $event;
  }

  isStatusWidgetConfigurationValid(isValid: boolean) {
    this.validStatusWidgetConfiguration = isValid;
  }

  isImageConfigurationValid($event: boolean) {
    this.validImageWidgetConfiguration = $event;
  }

  isCommandWidgetConfigurationValid($event: boolean) {
    this.validCommandWidgetConfiguration = $event;
  }

  doesCommandWidgetHaveGenericGatewaySelected($event: boolean) {
    this.hasGenericGatewaySelected = $event;
  }

  public isDisabledSelector(widgetType: UserDefinedWidgetType): boolean {
    switch (widgetType) {
      case UserDefinedWidgetType.MAP:
        return !this.machine.isLocationAvailable() || this.existingWidgetTypes.includes(UserDefinedWidgetType.MAP);
      default:
        return false;
    }
  }

  public getHintText(widgetType: UserDefinedWidgetType) {
    switch (widgetType) {
      case UserDefinedWidgetType.MAP: {
        return this.machine.isLocationAvailable() ?
            this.translate.instant('Widget disabled - Map widget can only be used once per user defined page.') :
            this.translate.instant('Widget disabled - The current machine has no address attached.');
      }
      default:
        return '';
    }
  }

  public shouldDisableSaveButton() {
    if (!this.isChartSetting || this.isWidgetNameNotSet || this.widgetNameIsUsed ||
        Array.from(this.hasDeletedSignalsMap.values()).some(value => value)) {
      return true;
    }

    switch (this.userDefinedWidget.widgetDetails.widgetType) {
      case UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS:
        return !this.validLineWidgetConfiguration;
      case UserDefinedWidgetType.GAUGE:
        return !this.validGaugeWidgetConfiguration;
      case UserDefinedWidgetType.TEXT:
        return !this.validTextWidgetConfiguration;
      case UserDefinedWidgetType.BAR:
        return !this.validBarWidgetConfiguration;
      case UserDefinedWidgetType.PIE:
        return !this.validPieWidgetConfiguration;
      case UserDefinedWidgetType.STATUS:
        return !this.validStatusWidgetConfiguration;
      case UserDefinedWidgetType.IMAGE:
        return !this.validImageWidgetConfiguration;
      case UserDefinedWidgetType.MAP:
        return !this.validMapWidgetConfiguration;
      case UserDefinedWidgetType.COMMAND:
        return !this.validCommandWidgetConfiguration || !this.hasGenericGatewaySelected;

        //not implemented yet
      case UserDefinedWidgetType.ADMIN_CHARTS:
      case UserDefinedWidgetType.FLATNESS:
      case UserDefinedWidgetType.ANALYSIS_LINE:
        return true;
    }
  }


  private trimLowerSpace(name: string): string {
    if (!name || name.isNullOrWhitespace()) {
      return '';
    }
    return name.trimWhiteSpaces().toLowerCase();
  }

  private convertSignalsObjectToArray(signals: any): Signal[] {
    if (!signals) {
      return [];
    }
    const signalsArray: Signal[] = [];
    const signalsKeys = Object.keys(signals);
    signalsKeys.forEach(element => {
      signalsArray.push(signals[element]);
    });
    return signalsArray;
  }
}
